import {ReactNode} from 'react';
import {
    Avatar,
	Box,
	BoxProps,
    Grid,
    GridItem,
	HStack,
	Link,
	Text,
	useRadioGroup
} from '@chakra-ui/react';
import {RadioCard} from "./SelectableCard";
import {EmptyState, L5Button} from "./CustomButton";

const choices = [
    {
        desc: "Grant any company user",
        title: "Any user",
        value: "all"
    },
    {
        desc: "Grant only one company user",
        title: "Specific user",
        value: "single"
    }
];
type NoUserProps = {title: string, desc: string, meta: NoUserMeta};
type NoUserMeta = {link?: ReactNode, reload?: () => void} | null;
type UserCardProps = {
    title: string
    subtitle: string
    url?: string
    minimal?: boolean
    children?: ReactNode
} & BoxProps;
const accountUrl = process.env.REACT_APP_ACCOUNT_APP;

export function UserCard(props: UserCardProps) {
    return <HStack align="center" spacing="1rem">
        {
            !props.minimal && <Avatar
                size="md"
                src={props.url}
                name={props.title}
            />
        }
        <Box className="stack" css={{"--s-gap": ".5rem"}}>
            <Text fontWeight="bold" textTransform="capitalize">
                {props.title}
            </Text>
            {!props.minimal && <Text>{props.subtitle}</Text>}
            {!props.minimal && props.children}
        </Box>
    </HStack>
}

export function NoUser(props: NoUserProps & BoxProps & {hideIcon?: boolean}) {
    return <EmptyState {...props} title={props.title} desc={props.desc} gap={props.gap ?? "1rem"}>
        {props.children}
        {props.meta?.link && props.meta.link}
        {props.meta?.reload && <L5Button type="primary" onClick={props.meta.reload}>
            Try Again
        </L5Button>}
    </EmptyState>;
}

export function UserCreationLink() {
    return <Text fontSize={"md"} color="dark.100" style={{textWrap: "balance"}}>
        Click &nbsp;
        <Link
            color="primary.500"
            cursor="pointer"
            fontWeight="bold"
            href={`${accountUrl}/add-user`}
            target="_blank"
        >here</Link> to add users.
    </Text>;
}

export function ChoiceRadio(props: {choice?: string, onChange: (v: string) => void}) {
    const {getRootProps, getRadioProps} = useRadioGroup({
        name: "breaker-choice",
        defaultValue: props.choice ?? "all",
        onChange: props.onChange
    });
    return <div className="stack">
        <Text fontWeight="bold" fontSize="1.1em" textTransform="capitalize">who can break the seal ?</Text>
        <HStack
            gap="1rem"
            align="stretch"
            justify="space-between"
            {...getRootProps()}
        >
            {choices.map((v) => <RadioCard
                key={v.value}
                value={v.value}
                radioProps={getRadioProps({value: v.value})}
                borderRadius="8px"
                border="2px solid var(--chakra-colors-gray-300)"
            >
                <div className="stack">
                    <Text fontWeight="bold" textTransform="capitalize">{v.title}</Text>
                    <Text>{v.desc}</Text>
                </div>
            </RadioCard>)}
        </HStack>
    </div>
}

function LoginHint() {
    let link = new URL(`${process.env.REACT_APP_ADMIRAL_APP}`);
    link.searchParams.set(
        "from",
        encodeURIComponent(window.location.href)
    );
    return <Text fontSize={"md"} textAlign="center" style={{textWrap: "pretty"}}>
        Note: if the issue persist, consider signing in&nbsp;
        <Link
            color="primary.500"
            fontWeight="bold"
            href={link.href}
        >on Admiral</Link> to ensure you're authenticated.
    </Text>;
}

export function InfoFetchingError(p: NoUserMeta) {
    return <Grid templateRows="1fr auto" minH="100dvb">
        <GridItem display="flex" alignItems="center" justifyContent="center">
            <NoUser
                desc="We’re having trouble fetching your account details right now. This might be due to a temporary connection issue or a problem with our servers."
                meta={{reload: p?.reload, link: p?.link ?? <LoginHint/>}}
                title="Unable to Retrieve Your Information"
                maxW="min(512px, 100%)"
                h="60dvb"
            />
        </GridItem>
    </Grid>;
}
